import { useEffect, useRef, useState } from "react";

const INPUT = "border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-full";

const ORIGENS = [
    { valor: "TODOS", rotulo: "Todos" },
    { valor: "USUARIOS", rotulo: "Usuários do sistema" },
    { valor: "FORTES", rotulo: "Funcionários (Fortes)" },
];

const ROTULO_ORIGEM = { USUARIO: "Sistema", FORTES: "Fortes" };

/**
 * Nome do responsável em texto livre, com consulta opcional a usuários do sistema e funcionários do
 * Fortes. Escolher uma sugestão só preenche nome e cargo; qualquer nome digitado é aceito.
 *
 * `onChange({ nome, cargo })` recebe sempre os dois valores.
 */
export default function SeletorResponsavel({ service, nome, cargo, onChange, disabled, placeholder }) {
    const [aberto, setAberto] = useState(false);
    const [origem, setOrigem] = useState("TODOS");
    const [itens, setItens] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [fortesIndisponivel, setFortesIndisponivel] = useState(false);
    const [consultou, setConsultou] = useState(false);
    const fechar = useRef(null);

    const termo = (nome || "").trim();

    useEffect(() => {
        if (!aberto || termo.length < 2) {
            setItens([]);
            setConsultou(false);
            return undefined;
        }
        let vigente = true;
        const espera = setTimeout(async () => {
            setCarregando(true);
            try {
                const res = await service.sugerirResponsaveis(termo, origem);
                if (!vigente) return;
                setItens(res.data?.itens || []);
                setFortesIndisponivel(!!res.data?.fortesIndisponivel);
                setConsultou(true);
            } catch (e) {
                if (vigente) {
                    setItens([]);
                    setConsultou(true);
                }
            } finally {
                if (vigente) setCarregando(false);
            }
        }, 350);
        return () => {
            vigente = false;
            clearTimeout(espera);
        };
    }, [aberto, termo, origem, service]);

    const escolher = (item) => {
        onChange({ nome: item.nome, cargo: item.cargo || "" });
        setAberto(false);
        setItens([]);
    };

    return (
        <div
            className="relative"
            onBlur={() => { fechar.current = setTimeout(() => setAberto(false), 150); }}
            onFocus={() => clearTimeout(fechar.current)}
        >
            <input
                className={INPUT}
                value={nome ?? ""}
                disabled={disabled}
                maxLength={200}
                placeholder={placeholder || "Digite o nome ou pesquise em usuários / funcionários"}
                onChange={e => { onChange({ nome: e.target.value, cargo: cargo ?? "" }); setAberto(true); }}
                onFocus={() => { if (termo.length >= 2) setAberto(true); }}
                onKeyDown={e => { if (e.key === "Escape") setAberto(false); }}
            />

            {aberto && termo.length >= 2 && (
                <div className="absolute z-30 left-0 right-0 mt-1 rounded-md border border-border bg-card shadow-lg text-sm">
                    <div className="flex flex-wrap gap-1 p-2 border-b border-border">
                        {ORIGENS.map(o => (
                            <button
                                key={o.valor}
                                type="button"
                                onClick={() => setOrigem(o.valor)}
                                className={`px-2 py-0.5 rounded-full text-xs border ${origem === o.valor
                                    ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                            >
                                {o.rotulo}
                            </button>
                        ))}
                    </div>

                    <ul className="max-h-56 overflow-auto">
                        {carregando && <li className="px-3 py-2 text-muted-foreground">Buscando...</li>}
                        {!carregando && itens.map((i, idx) => (
                            <li
                                key={`${i.origem}-${i.nome}-${idx}`}
                                className="px-3 py-2 cursor-pointer hover:bg-muted flex items-start justify-between gap-2"
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => escolher(i)}
                            >
                                <span>
                                    <strong>{i.nome}</strong>
                                    {i.cargo && <span className="text-muted-foreground"> · {i.cargo}</span>}
                                    {i.origem === "FORTES" && i.detalhe && (
                                        <span className="block text-xs text-muted-foreground">{i.detalhe}</span>
                                    )}
                                </span>
                                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">
                                    {ROTULO_ORIGEM[i.origem] || i.origem}
                                </span>
                            </li>
                        ))}
                        {!carregando && consultou && itens.length === 0 && (
                            <li className="px-3 py-2 text-muted-foreground">
                                Nenhum resultado. O nome digitado será usado como está.
                            </li>
                        )}
                    </ul>

                    {fortesIndisponivel && origem !== "USUARIOS" && (
                        <p className="px-3 py-2 text-xs text-amber-700 border-t border-border">
                            A base de funcionários do Fortes não pôde ser consultada agora.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
